import { useCallback, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRememberFilters } from "@/hooks/useRememberFilters";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExpandingSearch } from "@/components/ui/expanding-search";
import { SearchChip } from "@/components/ui/search-chip";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Filter, Warehouse } from "lucide-react";
import { AddFilterMenu } from "@/components/ui/add-filter-menu";
import { ChipFilter } from "@/components/ui/chip-filter";
import { ConfirmDeleteButton } from "@/components/ui/confirm-delete-button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AddressFields, formatAddress } from "@/components/forms/AddressFields";
import { Location, Customer } from "@/types/tms";
import { useCRUDMutations } from "@/hooks/useCRUDMutations";
import { geocodeAddress, buildAddressString } from "@/lib/geocode";
import { getLocationDisplayName } from "@/lib/location-utils";
import { archiveLocations, countActiveOrdersForLocations } from "@/lib/location-archive";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import { toast } from "sonner";
import { LocationDetailSheet } from "@/components/locations/LocationDetailSheet";
import { useUrlFilterState, useUrlArrayFilterState } from "@/hooks/useUrlFilterState";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { TablePagination } from "@/components/ui/table-pagination";
import { useTablePagination } from "@/hooks/useTablePagination";
import { LocationCard } from "@/components/locations/LocationCard";

interface LocationFormData {
  customer_id: string;
  short_code: string;
  street_address: string;
  city: string;
  province_state: string;
  postal_zip_code: string;
  country: string;
  track_in_yms: boolean;
  use_latlng: boolean;
  origin_latitude_input: string;
  origin_longitude_input: string;
  destination_latitude_input: string;
  destination_longitude_input: string;
}

export default function Locations() {
  const queryClient = useQueryClient();
  useRememberFilters();
  const [searchQuery, setSearchQuery] = useUrlFilterState<string>("q", "");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const ignoreAutoCloseRef = useRef<string | null>(null);
  const [ymsFilter, setYmsFilter] = useUrlArrayFilterState("yms");
  const [manuallyAddedFilters, setManuallyAddedFilters] = useState<string[]>([]);
  const activeFilters = (() => {
    const chips: string[] = [];
    if (!ymsFilter.includes("all")) chips.push("yms");
    for (const k of manuallyAddedFilters) if (!chips.includes(k)) chips.push(k);
    return chips;
  })();
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState<LocationFormData>({
    customer_id: "",
    short_code: "",
    street_address: "",
    city: "",
    province_state: "",
    postal_zip_code: "",
    country: "",
    track_in_yms: false,
    use_latlng: false,
    origin_latitude_input: "",
    origin_longitude_input: "",
    destination_latitude_input: "",
    destination_longitude_input: "",
  });

  const resetForm = () => {
    setFormData({
      customer_id: "", short_code: "", street_address: "", city: "",
      province_state: "", postal_zip_code: "", country: "", track_in_yms: false,
      use_latlng: false,
      origin_latitude_input: "", origin_longitude_input: "",
      destination_latitude_input: "", destination_longitude_input: "",
    });
    setEditingLocation(null);
    setIsDialogOpen(false);
  };

  const { data: locations, isLoading } = useQuery({
    queryKey: ["locations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("locations")
        .select("*, customer:customers(*)")
        .eq("archived", false)
        .order("short_code");
      if (error) throw error;
      return data as Location[];
    },
  });

  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("organization_name");
      if (error) throw error;
      return data as Customer[];
    },
  });

  const YMS_FILTER_OPTIONS = [
    { label: "Enabled", value: "enabled" },
    { label: "Not enabled", value: "not_enabled" },
  ];

  const availableFilterCategories = [
    { key: "yms", label: "YMS Tracking", icon: <Warehouse className="h-3.5 w-3.5" /> },
  ].filter((c) => !activeFilters.includes(c.key));

  const removeFilter = (key: string) => {
    setManuallyAddedFilters((prev) => prev.filter((k) => k !== key));
    if (key === "yms") setYmsFilter(["all"]);
  };

  const filteredLocations = locations?.filter((l) => {
    const displayName = getLocationDisplayName(l).toLowerCase();
    const q = searchQuery.toLowerCase();
    const matchesSearch = displayName.includes(q) || l.city?.toLowerCase().includes(q);
    const matchesYms = ymsFilter.includes("all") || ymsFilter.some((f) =>
      f === "enabled" ? l.track_in_yms : !l.track_in_yms
    );
    return matchesSearch && matchesYms;
  });

  const { selectedIds, toggle, toggleAll, clear, allSelected, someSelected, count } = useBulkSelection(filteredLocations);


  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    pageRows: pagedLocations,
  } = useTablePagination(filteredLocations);

  /**
   * Resolve the four coordinate values for a submission. In address mode
   * we geocode and seed origin + destination identically (users split them
   * manually afterwards). In Lat/Lng mode the four inputs are taken as-is.
   */
  type Coords = {
    origin_latitude: number;
    origin_longitude: number;
    destination_latitude: number;
    destination_longitude: number;
    // Legacy mirror for the deprecated single column — kept in sync until
    // the follow-up migration drops it.
    latitude: number;
    longitude: number;
  };
  const resolveCoords = async (data: LocationFormData): Promise<Coords | null> => {
    if (data.use_latlng) {
      const oLat = parseFloat(data.origin_latitude_input);
      const oLng = parseFloat(data.origin_longitude_input);
      const dLat = parseFloat(data.destination_latitude_input);
      const dLng = parseFloat(data.destination_longitude_input);
      if ([oLat, oLng, dLat, dLng].some((n) => isNaN(n))) return null;
      return {
        origin_latitude: oLat, origin_longitude: oLng,
        destination_latitude: dLat, destination_longitude: dLng,
        latitude: oLat, longitude: oLng,
      };
    }
    const addressStr = buildAddressString(data);
    if (!addressStr) return null;
    const coords = await geocodeAddress(addressStr);
    if (!coords) return null;
    return {
      origin_latitude: coords.latitude, origin_longitude: coords.longitude,
      destination_latitude: coords.latitude, destination_longitude: coords.longitude,
      latitude: coords.latitude, longitude: coords.longitude,
    };
  };

  const [pendingCoords, setPendingCoords] = useState<Coords | null>(null);

  const { createMutation, updateMutation } = useCRUDMutations<LocationFormData>({
    tableName: "locations",
    queryKey: "locations",
    entityLabel: "Location",
    transformInsert: (data) => {
      const {
        use_latlng,
        origin_latitude_input, origin_longitude_input,
        destination_latitude_input, destination_longitude_input,
        ...rest
      } = data;
      return { ...rest, customer_id: data.customer_id || null, ...(pendingCoords ?? {}) };
    },
    transformUpdate: (data) => {
      const {
        use_latlng,
        origin_latitude_input, origin_longitude_input,
        destination_latitude_input, destination_longitude_input,
        ...rest
      } = data;
      return { ...rest, customer_id: data.customer_id || null, ...(pendingCoords ?? {}) };
    },
    onCreateSuccess: () => { setPendingCoords(null); resetForm(); },
    onUpdateSuccess: () => { setPendingCoords(null); resetForm(); },
  });

  // Locations are never hard-deleted — archiving keeps the row so historical
  // orders/legs that reference it still resolve, while hiding it everywhere.
  const archiveMutation = useMutation({
    mutationFn: (ids: string[]) => archiveLocations(ids),
    onSuccess: (count) => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      toast.success(`Archived ${count} location${count === 1 ? "" : "s"}`);
      clear();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  /** Active-order counts keyed by location id, loaded when a confirm opens. */
  const [activeOrderCounts, setActiveOrderCounts] = useState<Record<string, number>>({});
  const [pendingBulkArchive, setPendingBulkArchive] = useState<{
    ids: string[];
    activeOrders: number;
  } | null>(null);

  const preflightLocation = useCallback(async (id: string) => {
    try {
      const count = await countActiveOrdersForLocations([id]);
      setActiveOrderCounts((prev) => ({ ...prev, [id]: count }));
    } catch (err) {
      console.error("Location archive preflight failed:", err);
    }
  }, []);

  const archiveDescription = (label: string, activeOrders: number | undefined) => (
    <div className="space-y-2">
      <p>{`Archive "${label}"? It will be hidden from all lists, filters, and order forms. Existing orders keep their address.`}</p>
      {!!activeOrders && (
        <p className="text-destructive">
          {activeOrders} active order{activeOrders === 1 ? "" : "s"} still reference
          {activeOrders === 1 ? "s" : ""} this location.
        </p>
      )}
    </div>
  );

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    const hasOrigin = location.origin_latitude != null && location.origin_longitude != null;
    const hasDest = location.destination_latitude != null && location.destination_longitude != null;
    const hasAddress = !!(location.street_address || location.city);
    const useLatLng = (hasOrigin || hasDest) && !hasAddress;
    setFormData({
      customer_id: location.customer_id || "",
      short_code: location.short_code,
      street_address: location.street_address || "",
      city: location.city || "",
      province_state: location.province_state || "",
      postal_zip_code: location.postal_zip_code || "",
      country: location.country || "",
      track_in_yms: location.track_in_yms ?? false,
      use_latlng: useLatLng,
      origin_latitude_input: location.origin_latitude != null ? String(location.origin_latitude) : "",
      origin_longitude_input: location.origin_longitude != null ? String(location.origin_longitude) : "",
      destination_latitude_input: location.destination_latitude != null ? String(location.destination_latitude) : "",
      destination_longitude_input: location.destination_longitude != null ? String(location.destination_longitude) : "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.street_address.trim() || !formData.city.trim()) {
      toast.error("Street address and city are required so orders using this location validate correctly");
      return;
    }
    if (formData.use_latlng) {
      const pairs: Array<[string, string, string]> = [
        ["Origin latitude", formData.origin_latitude_input, "lat"],
        ["Origin longitude", formData.origin_longitude_input, "lng"],
        ["Destination latitude", formData.destination_latitude_input, "lat"],
        ["Destination longitude", formData.destination_longitude_input, "lng"],
      ];
      for (const [label, raw, kind] of pairs) {
        const n = parseFloat(raw);
        if (isNaN(n)) { toast.error(`${label} is required`); return; }
        if (kind === "lat" && (n < -90 || n > 90)) { toast.error(`${label} must be between -90 and 90`); return; }
        if (kind === "lng" && (n < -180 || n > 180)) { toast.error(`${label} must be between -180 and 180`); return; }
      }
    }
    const coords = await resolveCoords(formData);
    if (!coords) {
      toast.error(formData.use_latlng
        ? "Could not parse latitude/longitude values"
        : "Could not geocode address — try entering Lat/Lng manually");
      return;
    }
    setPendingCoords(coords);
    if (editingLocation) {
      updateMutation.mutate({ id: editingLocation.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  return (
    <AppLayout
      title="Locations"
      actions={
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-2" /> Add Location</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingLocation ? "Edit Location" : "Add Location"}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Customer</Label>
                  <Select value={formData.customer_id} onValueChange={(v) => setFormData({ ...formData, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="short_code">Short Code *</Label>
                  <Input id="short_code" value={formData.short_code} onChange={(e) => setFormData({ ...formData, short_code: e.target.value })} placeholder="e.g., ELP1" required />
                </div>
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Address</h4>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="use_latlng" className="cursor-pointer text-sm font-normal">Use Lat/Lng</Label>
                    <Switch
                      id="use_latlng"
                      checked={formData.use_latlng}
                      onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, use_latlng: checked }))}
                    />
                  </div>
                </div>
                {formData.use_latlng ? (
                  <div className="space-y-4">
                    <AddressFields
                      prefix="location"
                      values={formData}
                      onChange={(field, value) => setFormData((prev) => ({ ...prev, [field]: value }))}
                      onBatchChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
                      required
                    />
                    {(["origin", "destination"] as const).map((side) => {
                      const latField = `${side}_latitude_input` as const;
                      const lngField = `${side}_longitude_input` as const;
                      const label = side === "origin" ? "Origin" : "Destination";
                      const helper = side === "origin"
                        ? "Used as the pickup coordinate when this location is a ship-from."
                        : "Used as the drop-off coordinate when this location is a ship-to.";
                      return (
                        <div key={side} className="space-y-2 rounded-md border p-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-semibold">{label} coordinates *</Label>
                            {side === "destination" && (
                              <Button
                                type="button" variant="ghost" size="sm"
                                onClick={() => setFormData((prev) => ({
                                  ...prev,
                                  destination_latitude_input: prev.origin_latitude_input,
                                  destination_longitude_input: prev.origin_longitude_input,
                                }))}
                              >Copy from origin</Button>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{helper}</p>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <Label htmlFor={latField}>Latitude</Label>
                              <Input
                                id={latField}
                                value={formData[latField]}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v.includes(",")) {
                                    const [lat, lng] = v.split(",").map((s) => s.trim());
                                    setFormData((prev) => ({
                                      ...prev, [latField]: lat,
                                      [lngField]: lng ?? prev[lngField],
                                    } as LocationFormData));
                                  } else {
                                    setFormData((prev) => ({ ...prev, [latField]: v } as LocationFormData));
                                  }
                                }}
                                placeholder="34.052235"
                                required
                              />
                            </div>
                            <div>
                              <Label htmlFor={lngField}>Longitude</Label>
                              <Input
                                id={lngField}
                                value={formData[lngField]}
                                onChange={(e) => setFormData((prev) => ({ ...prev, [lngField]: e.target.value } as LocationFormData))}
                                placeholder="-118.243683"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <AddressFields
                    prefix="location"
                    values={formData}
                    onChange={(field, value) => setFormData((prev) => ({ ...prev, [field]: value }))}
                    onBatchChange={(updates) => setFormData((prev) => ({ ...prev, ...updates }))}
                    required
                  />
                )}
              </div>
              <div className="flex items-center space-x-2 border-t pt-4">
                <Checkbox
                  id="track_in_yms"
                  checked={formData.track_in_yms}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, track_in_yms: checked === true }))}
                />
                <Label htmlFor="track_in_yms" className="cursor-pointer">Track in YMS (Yard Management System)</Label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>{editingLocation ? "Update" : "Create"}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center gap-2 flex-wrap">
          <SearchChip query={searchQuery} onClear={() => setSearchQuery("")} />
          {activeFilters.includes("yms") && (
            <ChipFilter
              label="YMS Tracking"
              icon={<Warehouse className="h-3.5 w-3.5" />}
              value={ymsFilter}
              onChange={(v) => setYmsFilter(Array.isArray(v) ? v : [v])}
              options={YMS_FILTER_OPTIONS}
              onRemove={() => removeFilter("yms")}
              multiSelect
              open={openFilterKey === "yms"}
              onOpenChange={(o) => {
                if (!o && ignoreAutoCloseRef.current === "yms") {
                  ignoreAutoCloseRef.current = null;
                  return;
                }
                if (!o) {
                  setOpenFilterKey((prev) => (prev === "yms" ? null : prev));
                } else {
                  setOpenFilterKey("yms");
                }
              }}
            />
          )}
          <AddFilterMenu
            categories={availableFilterCategories}
            onSelect={(key) => {
              setManuallyAddedFilters((prev) => (prev.includes(key) ? prev : [...prev, key]));
              window.setTimeout(() => {
                ignoreAutoCloseRef.current = key;
                setOpenFilterKey(key);
                window.setTimeout(() => {
                  if (ignoreAutoCloseRef.current === key) ignoreAutoCloseRef.current = null;
                }, 250);
              }, 50);
            }}
            iconOnly={activeFilters.length > 0}
          />
          <div className="ml-auto flex items-center gap-3">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Showing {filteredLocations?.length ?? 0} location{(filteredLocations?.length ?? 0) === 1 ? "" : "s"}</span>
            <ExpandingSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search locations..." />
          </div>
        </div>

        <div>
          <BulkActionBar count={count} onClear={clear} entityLabel="location">
            <Button
              variant="ghost"
              size="sm"
              className="text-primary-foreground hover:bg-primary-foreground/20 hover:text-primary-foreground"
              onClick={async () => {
                const ids = Array.from(selectedIds);
                let activeOrders = 0;
                try {
                  activeOrders = await countActiveOrdersForLocations(ids);
                } catch (err) {
                  console.error("Location archive preflight failed:", err);
                }
                setPendingBulkArchive({ ids, activeOrders });
              }}
              disabled={archiveMutation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Archive
            </Button>
          </BulkActionBar>
          <div className={`rounded-lg border bg-card ${count > 0 ? "rounded-t-none border-t-0" : ""}`}>
          <ResponsiveTableShell
            cards={
              <div className="flex flex-col gap-3 p-3">
                {isLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading...</div>
                ) : pagedLocations.length ? (
                  pagedLocations.map((location) => (
                    <LocationCard
                      key={location.id}
                      location={location}
                      displayName={getLocationDisplayName(location)}
                      isChecked={selectedIds.has(location.id)}
                      onToggle={toggle}
                      onClick={setSelectedLocation}
                      onEdit={handleEdit}
                      onDelete={(id) => archiveMutation.mutate([id])}
                      deleteTitle="Archive location?"
                      deleteConfirmLabel="Archive"
                      onDeleteOpenChange={(open) => { if (open) preflightLocation(location.id); }}
                      deleteDescription={archiveDescription(
                        getLocationDisplayName(location),
                        activeOrderCounts[location.id],
                      )}
                    />
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {(searchQuery.trim() || !ymsFilter.includes("all"))
                      ? "No locations match your filter criteria"
                      : "No locations found"}
                  </div>
                )}
              </div>
            }
            table={
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    aria-label="Select all"
                    {...(someSelected ? { "data-state": "indeterminate" } : {})}
                  />
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Address</TableHead>
                <TableHead>YMS Tracking</TableHead>
                <TableHead>Coordinates</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
              ) : pagedLocations.length ? (
                pagedLocations.map((location) => (
                  <TableRow key={location.id} className="cursor-pointer" onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, input, a, [role="checkbox"]')) return;
                    setSelectedLocation(location);
                  }}>
                    <TableCell className="w-[40px]">
                      <Checkbox
                        checked={selectedIds.has(location.id)}
                        onCheckedChange={() => toggle(location.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{getLocationDisplayName(location)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {location.customer?.organization_name || "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatAddress(location)}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {location.track_in_yms ? "Enabled" : "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {(() => {
                        const o = location.origin_latitude != null && location.origin_longitude != null
                          ? `${location.origin_latitude.toFixed(6)}, ${location.origin_longitude.toFixed(6)}`
                          : "—";
                        const d = location.destination_latitude != null && location.destination_longitude != null
                          ? `${location.destination_latitude.toFixed(6)}, ${location.destination_longitude.toFixed(6)}`
                          : "—";
                        return (
                          <span className="flex flex-col gap-0.5">
                            <span><span className="text-[10px] uppercase tracking-wide opacity-60 mr-1">O</span>{o}</span>
                            <span><span className="text-[10px] uppercase tracking-wide opacity-60 mr-1">D</span>{d}</span>
                          </span>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(location)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <ConfirmDeleteButton
                          onConfirm={() => archiveMutation.mutate([location.id])}
                          entityName="location"
                          entityLabel={getLocationDisplayName(location)}
                          title="Archive location?"
                          confirmLabel="Archive"
                          onOpenChange={(open) => { if (open) preflightLocation(location.id); }}
                          description={archiveDescription(
                            getLocationDisplayName(location),
                            activeOrderCounts[location.id],
                          )}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">{(searchQuery.trim() || !ymsFilter.includes("all")) ? "No locations match your filter criteria" : "No locations found"}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
            }
          />
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
        </div>
      </div>

      <LocationDetailSheet
        location={selectedLocation}
        open={!!selectedLocation}
        onOpenChange={(open) => { if (!open) setSelectedLocation(null); }}
        onEdit={handleEdit}
      />

      <AlertDialog
        open={!!pendingBulkArchive}
        onOpenChange={(open) => { if (!open) setPendingBulkArchive(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Archive {pendingBulkArchive?.ids.length ?? 0} location
              {(pendingBulkArchive?.ids.length ?? 0) === 1 ? "" : "s"}?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  They will be hidden from all lists, filters, and order forms.
                  Existing orders keep their address.
                </p>
                {!!pendingBulkArchive?.activeOrders && (
                  <p className="text-destructive">
                    {pendingBulkArchive.activeOrders} active order
                    {pendingBulkArchive.activeOrders === 1 ? "" : "s"} still reference
                    {pendingBulkArchive.activeOrders === 1 ? "s" : ""} the selected
                    location{pendingBulkArchive.ids.length === 1 ? "" : "s"}.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingBulkArchive) archiveMutation.mutate(pendingBulkArchive.ids);
                setPendingBulkArchive(null);
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
